import React from 'react';
import {
  useReactTable,
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  SortingState,
  getFilteredRowModel,
} from '@tanstack/react-table';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import IconButton from '@mui/material/IconButton';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import Spinner from './Spinner';
import './Table.css';

interface BaseTanStackTableProps {
  columns: ColumnDef<any>[];
  data: any[];
  pageCount?: number;
  pagination: { pageIndex: number; pageSize: number };
  setPagination: React.Dispatch<React.SetStateAction<{ pageIndex: number; pageSize: number }>>;
  sorting: SortingState;
  setSorting: React.Dispatch<React.SetStateAction<SortingState>>;
  globalFilter: string;
  setGlobalFilter: (value: string) => void;
  isLoading?: boolean;
  manualPagination?: boolean;
  manualSorting?: boolean;
  toolbar?: React.ReactNode;
  renderActions?: (row: any) => React.ReactNode;
}

const BaseTanStackTable: React.FC<BaseTanStackTableProps> = ({
  columns,
  data,
  pageCount,
  pagination,
  setPagination,
  sorting,
  setSorting,
  globalFilter,
  setGlobalFilter,
  isLoading = false,
  manualPagination = true,
  manualSorting = true,
  toolbar,
  renderActions,
}) => {
  const table = useReactTable({
    data,
    columns,
    ...(manualPagination && pageCount !== undefined ? { pageCount } : {}),
    manualPagination,
    manualSorting,
    state: { sorting, pagination, globalFilter },
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  const getSortingIcon = (header: any) => {
    if (!header.column.getCanSort()) return null;
    return header.column.getIsSorted() === 'asc'  ? <KeyboardArrowUpIcon />   :
           header.column.getIsSorted() === 'desc' ? <KeyboardArrowDownIcon /> :
           <UnfoldMoreIcon />;
  };

  return (
    <>
      {toolbar && (
        <div className="page-toolbar" style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          {toolbar}
        </div>
      )}

      {isLoading ? (
        <Spinner />
      ) : (
        <div className="table-container">
          <table>
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      onClick={header.column.getToggleSortingHandler()}
                      style={{ cursor: header.column.getCanSort() ? 'pointer' : 'default' }}
                    >
                      {header.isPlaceholder ? null : (
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {getSortingIcon(header)}
                        </div>
                      )}
                    </th>
                  ))}
                  {renderActions && <th>Ações</th>}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row) => (
                <tr key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                  {renderActions && <td>{renderActions(row.original)}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="table-pagination">
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <IconButton
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
            color="primary"
          >
            <NavigateBeforeIcon />
          </IconButton>
          <span>
            Página{' '}
            <strong>
              {table.getState().pagination.pageIndex + 1} de {table.getPageCount()}
            </strong>
          </span>
          <IconButton
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
            color="primary"
          >
            <NavigateNextIcon />
          </IconButton>
        </div>
        <FormControl variant="outlined" size="small" style={{ minWidth: 120 }}>
          <InputLabel id="page-size-label">Itens por página</InputLabel>
          <Select
            labelId="page-size-label"
            value={pagination.pageSize}
            onChange={(e) => setPagination({ pageIndex: 0, pageSize: Number(e.target.value) })}
            label="Itens por página"
          >
            {[10, 20, 30, 40, 50].map((size) => (
              <MenuItem key={size} value={size}>Mostrar {size}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>
    </>
  );
};

export default BaseTanStackTable;
